import { GenericField, InputProps } from "../Fields/GenericField";
import { useField } from "formik";
import { MinusIcon, PlusIcon } from "@heroicons/react/16/solid";
import * as Headless from "@headlessui/react";
import { cn } from "@/lib/utils";
import { useState } from "react";

export type StepperInputProps = InputProps & {
  min?: number;
  max?: number;
  step?: number;
  onChange?: (val: number) => void;
};

export function StepperInput({
  min = 0,
  max,
  step = 1,
  onChange,
  ...props
}: StepperInputProps) {
  const [field, meta, { setValue }] = useField<number>(props.name);
  const value = field.value ?? min;
  const showError = !!meta.error && meta.touched;

  // While focused, hold the raw text so the user can clear the field and type
  // freely (e.g. "12", "24") without it snapping back to min on each keystroke.
  const [draft, setDraft] = useState<string | null>(null);
  const display = draft ?? String(value);

  const canDecrement = value > min;
  const canIncrement = max === undefined || value < max;

  function clamp(n: number) {
    return Math.max(min, max !== undefined ? Math.min(max, n) : n);
  }

  async function handleDecrement() {
    if (!canDecrement) return;
    const newValue = clamp(value - step);
    await setValue(newValue);
    onChange?.(newValue);
  }

  async function handleIncrement() {
    if (!canIncrement) return;
    const newValue = clamp(value + step);
    await setValue(newValue);
    onChange?.(newValue);
  }

  async function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    setDraft(raw);
    if (raw === "") return; // allow empty while typing
    const n = parseInt(raw, 10);
    if (isNaN(n)) return;
    // Cap at max as you type, but don't force up to min mid-edit.
    const newValue = max !== undefined ? Math.min(max, n) : n;
    await setValue(newValue);
    onChange?.(newValue);
  }

  return (
    <GenericField viewNode={<span>{value}</span>} {...props}>
      <span
        data-slot="control"
        className={cn([
          "relative block w-full",
          "before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm",
          "dark:before:hidden",
          "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:focus-within:after:ring-2 sm:focus-within:after:ring-blue-500",
          "has-data-disabled:opacity-50 has-data-disabled:before:bg-zinc-950/5 has-data-disabled:before:shadow-none",
          showError && "before:shadow-red-500/10",
          "[&_input::-webkit-inner-spin-button]:appearance-none [&_input::-webkit-outer-spin-button]:appearance-none [&_input]:appearance-none",
        ])}
      >
        <Headless.Input
          type="number"
          value={display}
          onChange={handleInputChange}
          onFocus={(e) => e.target.select()}
          invalid={showError}
          min={min}
          max={max}
          className={cn([
            "relative block w-full appearance-none rounded-lg py-[calc(--spacing(2.5)-1px)] pl-[calc(--spacing(3.5)-1px)] pr-16 sm:py-[calc(--spacing(1.5)-1px)] sm:pl-[calc(--spacing(3)-1px)]",
            "text-base/6 text-zinc-950 placeholder:text-zinc-500 sm:text-sm/6 dark:text-white",
            "data-hover:border-zinc-950/20 dark:data-hover:border-white/20 border border-zinc-950/10 dark:border-white/10",
            "bg-transparent dark:bg-white/5",
            "focus:outline-hidden",
            "data-invalid:border-red-500 data-invalid:data-hover:border-red-500",
          ])}
        />
        <div className="absolute inset-y-0 right-2 z-10 flex items-center gap-1">
          <button
            type="button"
            onClick={handleDecrement}
            disabled={!canDecrement}
            className={cn(
              "flex h-6 w-6 items-center justify-center rounded transition-colors",
              canDecrement
                ? "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700"
                : "cursor-not-allowed text-zinc-300",
            )}
          >
            <MinusIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleIncrement}
            disabled={!canIncrement}
            className={cn(
              "flex h-6 w-6 items-center justify-center rounded transition-colors",
              canIncrement
                ? "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700"
                : "cursor-not-allowed text-zinc-300",
            )}
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        </div>
      </span>
    </GenericField>
  );
}
